import { validateUrlProtocol } from '@chronosphereio/chrono-ui';

export type IframeUrlValidation =
  | { safeUrl: string; validationError: null }
  | { safeUrl: null; validationError: string };

/**
 * Rejects two attack surfaces that the iframe sandbox cannot stop on its own:
 *
 *  1. Dangerous URL schemes (`javascript:`, `data:`, `vbscript:`, `file:`,
 *     `blob:`). Chrome routes `javascript:` iframe navigations through
 *     `script-src`, not `frame-src`, and these schemes inherit the parent's
 *     origin — combined with `allow-scripts` + `allow-same-origin` they would
 *     give the iframe access to `parent.document` and the viewer's session.
 *
 *  2. URLs that resolve to the parent's own origin (relative paths or
 *     absolute URLs pointing back at this app). With `allow-scripts` +
 *     `allow-same-origin`, a same-origin iframe can also reach
 *     `parent.document`. For genuinely cross-origin embeds, same-origin
 *     policy already isolates `parent.document` regardless of the sandbox
 *     token, so this check only fires on the dangerous case.
 */
export function validateExternalIframeUrl(url: string, parentOrigin: string): IframeUrlValidation {
  const schemeChecked = validateUrlProtocol(url);
  if (schemeChecked === null) {
    return { safeUrl: null, validationError: 'URL scheme is not allowed.' };
  }

  let resolved: URL;
  try {
    resolved = new URL(schemeChecked, parentOrigin);
  } catch {
    return { safeUrl: null, validationError: 'URL scheme is not allowed.' };
  }

  if (resolved.origin === parentOrigin) {
    return { safeUrl: null, validationError: 'Embedding the current site is not allowed.' };
  }

  return { safeUrl: schemeChecked, validationError: null };
}
